import React, { Component } from "react";

import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { IRosTopic } from "@formant/core/model/IRosTopic";
import { IValidation } from "@formant/core/model/IValidation";
import { rosTypeFloat64 } from "@formant/core/model/rosTypes";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";

import { Heading3 } from "../common/Heading3";
import { NumberField } from "../common/NumberField";
import { Switch } from "../common/Switch";
import { TeleopSlider } from "../Teleop/TeleopModule/TeleopSlider";
import {
    EditTeleopStreamPopup,
    IEditTeleopStreamPopupProps,
} from "./EditTeleopStreamPopup";
import * as styles from "./TeleopNumericConfiguration.scss";

export interface ITeleopNumericConfigurationProps extends IEditTeleopStreamPopupProps {
    streamName?: string;
    document: IDeviceConfigurationDocument;
    topics: IRosTopic[];
    validation?: IValidation;
    onChange: (document: IDeviceConfigurationDocument) => void;
}

const defaultMin = -1;
const defaultMax = 1;
const defaultStep = 0.1;
const defaultDefault = 0;

export class TeleopNumericConfiguration extends Component<ITeleopNumericConfigurationProps> {
    public render() {
        const { streamName, topics, onClose, visible } = this.props;

        const numericTopics = topics
            .filter(({ type }) => type === rosTypeFloat64)
            .map(_ => _.name);

        let streamType = "ROS";
        const stream = this.existingStream;

        if (!stream) {
            return false;
        }
        if ((stream as any).topicName === undefined) {
            streamType = "custom";
        }

        const { min, max, defaultValue, step, numericControlVisualization } =
            stream;

        return (
            <EditTeleopStreamPopup
                title={`Configuration for: ${streamName}`}
                onClose={onClose}
                visible={visible}
                className={styles["teleop-numeric-configuration"]}
            >
                {streamType === "ROS" && (
                    <>
                        <p>
                            Add an optional stream topic to represent the real
                            value
                        </p>
                        <TextField
                            value={
                                (stream as IDeviceTeleopRosStreamConfiguration)
                                    .valueTopicName
                            }
                            onChange={this.onChangeValueTopicName}
                            label="Topic name"
                            placeholder="Topic name - eg. /actual/value"
                            list={numericTopics}
                            autoFocus={true}
                            small={true}
                        />
                    </>
                )}
                {streamType === "custom" && (
                    <>
                        <p>
                            Add an optional stream to represent the real value
                        </p>
                        <TextField
                            value={
                                (
                                    stream as IDeviceTeleopCustomStreamConfiguration
                                ).valueStreamName
                            }
                            onChange={this.onChangeValueStreamName}
                            label="Stream name"
                            placeholder="Stream name - eg. actual.numeric"
                            autoFocus={true}
                            small={true}
                        />
                    </>
                )}
                <p>Slider minimum value</p>
                <NumberField
                    numberValue={min === undefined ? defaultMin : min}
                    onChangeNumber={this.onChangeMin}
                    autoFocus={true}
                    small={true}
                />
                <p>Slider maximum value</p>
                <NumberField
                    numberValue={max === undefined ? defaultMax : max}
                    onChangeNumber={this.onChangeMax}
                    label="Max"
                    small={true}
                />
                <p>Default value</p>
                <NumberField
                    numberValue={
                        defaultValue === undefined
                            ? defaultDefault
                            : defaultValue
                    }
                    onChangeNumber={this.onChangeDefault}
                    label="Default"
                    small={true}
                />
                <p>Step</p>
                <NumberField
                    numberValue={step === undefined ? defaultStep : step}
                    onChangeNumber={this.onChangeStep}
                    label="Step"
                    small={true}
                />

                <Switch
                    state={numericControlVisualization === "dial"}
                    onChange={this.onToggleVisMode}
                    stateLabels={["slider", "dial"]}
                    label="Visualization"
                    className={styles.visualization}
                />

                <div className={styles.preview}>
                    <Heading3>Preview</Heading3>
                    <TeleopSlider stream={stream} debug={true} />
                </div>
            </EditTeleopStreamPopup>
        );
    }

    private get existingStream() {
        const { streamName, document } = this.props;
        const customStream = document.teleop?.customStreams?.find(
            _ =>
                _.name === streamName &&
                _.rtcStreamType === "numeric" &&
                _.mode === "command",
        );

        const rosStream = document.teleop?.rosStreams?.find(
            _ =>
                _.topicName === streamName &&
                _.topicType === "std_msgs/Float64" &&
                _.mode === "command",
        );

        return customStream || rosStream;
    }

    private onChangeMin = (number: number | undefined) => {
        const { document, onChange } = this.props;
        const { existingStream } = this;

        if (!existingStream) {
            return;
        }

        existingStream.min = number;
        onChange({ ...document });
    };

    private onChangeMax = (number: number | undefined) => {
        const { document, onChange } = this.props;
        const { existingStream } = this;

        if (!existingStream || !number) {
            return;
        }

        existingStream.max = number;
        onChange({ ...document });
    };

    private onChangeDefault = (number: number | undefined) => {
        const { document, onChange } = this.props;
        const { existingStream } = this;

        if (!existingStream) {
            return;
        }

        existingStream.defaultValue = number;
        onChange({ ...document });
    };

    private onChangeStep = (number: number | undefined) => {
        const { document, onChange } = this.props;
        const { existingStream } = this;

        if (!existingStream) {
            return;
        }

        existingStream.step = number;
        onChange({ ...document });
    };

    private onToggleVisMode = (newState: boolean) => {
        const { document, onChange } = this.props;
        const { existingStream } = this;

        if (!existingStream) {
            return;
        }

        const numericControlVisualization =
            newState === true ? "dial" : "slider";

        existingStream.numericControlVisualization =
            numericControlVisualization;
        onChange({ ...document });
    };

    private onChangeValueTopicName = (
        event: React.ChangeEvent<InputElement>,
    ) => {
        const { document, onChange } = this.props;
        const { existingStream } = this;

        if (!existingStream) {
            return;
        }
        (existingStream as IDeviceTeleopRosStreamConfiguration).valueTopicName =
            event.target.value;
        onChange({ ...document });
    };

    private onChangeValueStreamName = (
        event: React.ChangeEvent<InputElement>,
    ) => {
        const { document, onChange } = this.props;
        const { existingStream } = this;

        if (!existingStream) {
            return;
        }
        (
            existingStream as IDeviceTeleopCustomStreamConfiguration
        ).valueStreamName = event.target.value;
        onChange({ ...document });
    };
}
